import React, { useEffect, useMemo, useState } from 'react';
import { Column, Row } from 'react-table';

import { AdminUserCouponDto, UserProfileDto } from 'services/data-contracts';
import TableBody from 'components/table/TableBody';
import { EditButton } from 'resources/styles/TableStyle';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import PaginationWrapper from '../Pagination';
import { useStoreWrapper } from 'stores/StoreHelper';
import { InputField, Row as SaladRow } from 'saladsoft-primitive';

interface UserCouponTableProps {
  userCoupons: AdminUserCouponDto[];
  onRowClick?: (userCoupon: AdminUserCouponDto) => void;
  onEditClick?: (userCoupon: AdminUserCouponDto) => void;
  onUserEmailChange?: (userCouponId: number, userEmail: string) => void;
  onDeleteClick?: (userCoupon: AdminUserCouponDto) => void;
}

// 테이블 데이터 타입 정의
const UserCouponTable = observer(
  ({
    userCoupons,
    onRowClick,
    onEditClick,
    onDeleteClick,
    onUserEmailChange,
  }: UserCouponTableProps) => {
    const { couponFilterStore } = useStoreWrapper();

    // 테이블 컬럼 정의
    const columns = useMemo(
      () => [
        {
          Header: 'No',
          accessor: 'id' as keyof AdminUserCouponDto,
          width: 60,
        },
        {
          Header: '쿠폰코드',
          accessor: 'code' as keyof AdminUserCouponDto,
          width: 100,
        },
        {
          Header: '쿠폰명',
          accessor: 'coupon.name' as keyof AdminUserCouponDto,
          width: 200,
        },
        {
          Header: '한옥',
          accessor: 'coupon.listingSummary' as keyof AdminUserCouponDto,
          width: 200,
        },
        {
          Header: '할인',
          accessor: 'discountType' as keyof AdminUserCouponDto,
          width: 200,
          Cell: ({ value, row }: { value: string; row: any }) => {
            const price = row.original.coupon?.price;
            return `${price?.toLocaleString()}${row.original.coupon?.discountType === 'FIXED' ? '원' : '%'}`;
          },
        },

        {
          Header: '숙박기간',
          accessor: 'stayStartDate' as keyof AdminUserCouponDto,
          width: 120,
          Cell: ({ value, row }: { value: string; row: any }) => {
            return value ? new Date(value).toLocaleDateString('ko-KR') : '-';
          },
        },
        {
          Header: '회원이메일',
          accessor: 'username' as keyof AdminUserCouponDto,
          width: 250,
          Cell: ({ value, row }: { value: string; row: any }) => {
            const [currentValue, setCurrentValue] = useState(value);

            return (
              <InputField
                key={row.original.id}
                value={value}
                disabled={!!value}
                onClick={(e: any) => {
                  e.stopPropagation();
                }}
                onBlur={(e: any) => {
                  e.stopPropagation();
                  if (currentValue !== e.target.value) {
                    onUserEmailChange?.(row.original.id as number, e.target.value);
                  }
                }}
              />
            );
          },
        },
        {
          Header: '상태',
          accessor: 'status' as keyof AdminUserCouponDto,
          width: 80,
          Cell: ({ value }: { value: string }) => {
            switch (value) {
              case 'OPEN':
                return '사용가능';
              case 'USED':
                return '사용완료';
              case 'DISCARDED':
                return '사용취소';
              default:
                return '사용가능';
            }
          },
        },
        {
          Header: '만료일',
          accessor: 'coupon.expireDate' as keyof AdminUserCouponDto,
          width: 120,
          Cell: ({ value }: { value: string }) => {
            return value ? new Date(value).toLocaleDateString('ko-KR') : '-';
          },
        },
        {
          Header: '생성자',
          accessor: 'creator' as keyof AdminUserCouponDto,
          width: 120,
          Cell: ({ value }: { value: any }) => {
            return value ? value.username : '-';
          },
        },
        {
          Header: '생성일',
          accessor: 'createDate' as keyof AdminUserCouponDto,
          width: 120,
          Cell: ({ value }: { value: string }) => {
            return value ? new Date(value).toLocaleDateString('ko-KR') : '-';
          },
        },
        // {
        //   Header: '수정자',
        //   accessor: 'modifier' as keyof AdminUserCouponDto,
        //   width: 120,
        //   Cell: ({ value }: { value: any }) => {
        //     return value ? value.username : '-';
        //   },
        // },
        // {
        //   Header: '수정일',
        //   accessor: 'modifyDate' as keyof AdminUserCouponDto,
        //   width: 120,
        //   Cell: ({ value }: { value: string }) => {
        //     return value ? new Date(value).toLocaleDateString('ko-KR') : '-';
        //   },
        // },
        {
          Header: '비고',
          accessor: 'remark' as keyof AdminUserCouponDto,
          width: 120,
          Cell: ({ value }: { value: string }) => {
            return value ? (value.length > 20 ? value.substring(0, 20) + '...' : value) : '-';
          },
        },
        {
          Header: '삭제',
          id: 'edit',
          width: 80,
          Cell: ({ row }: { row: Row<AdminUserCouponDto> }) => (
            <EditButton
              style={{ background: 'var(--point-color)' }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick?.(row.original as AdminUserCouponDto);
              }}
            >
              삭제
            </EditButton>
          ),
        },
      ],
      [],
    ) as Column<AdminUserCouponDto>[];

    return (
      <div>
        <div style={{ paddingBottom: '100px' }}>
          <TableBody columns={columns} data={userCoupons} onRowClick={onRowClick} />
        </div>
        <PaginationContainer>
          <PaginationWrapper
            count={couponFilterStore.paginationInfo?.totalCount ?? 0}
            totalPages={couponFilterStore.paginationInfo?.totalPages ?? 0}
          />
        </PaginationContainer>
      </div>
    );
  },
);

const PaginationContainer = styled.div`
  margin-top: 20px;
  position: fixed;
  bottom: 0;
  z-index: 10;
  margin: auto;
  background: rgba(255, 255, 255, 1);
  backdrop-filter: blur(10px);
  left: 0;
  right: 0;

  display: flex;
  justify-content: center;
  align-items: center;
`;

export default UserCouponTable;
